import { createSignal } from '@pexip/signal';
import { extractor } from './extractor';
import { deleteItem, getItem, setItem } from './localStorage';
import { logger } from './logger';
import { getTypeOf, isArray, isBoolean, isNumber, isRecord, isString, isValid, } from './types';
const clone = (config) => Object.fromEntries(Object.entries(config).flatMap(([key, value]) => {
    if (isArray(value)) {
        return [[key, [...value]]];
    }
    if (isRecord(value)) {
        return [[key, { ...value }]];
    }
    if (isString(value) || isBoolean(value) || isNumber(value)) {
        return [[key, value]];
    }
    return [];
}));
export class ConfigManager {
    constructor(config, namespace) {
        this.config = config;
        this.namespace = namespace;
        this.loaded = false;
        this.signals = {};
        // Use a copy to freely mutate while still retaining posibility to reset to values from `config`.
        this.currentConfig = clone(config);
        // Runtime validation that all the values is something we can infer
        for (const key in config) {
            if (!Object.hasOwn(config, key) || !isValid(config[key])) {
                throw new TypeError(`Invalid value for "${key}", must be of 'Valid | Record<string, Valid> where Valid = string | string[] | boolean | number', got "${getTypeOf(config[key])}"`);
            }
        }
    }
    get(key) {
        this.isLoaded();
        return this.currentConfig[key];
    }
    set({ key, value, persist = false, emit = true, }) {
        this.isLoaded();
        this.currentConfig[key] = value;
        // HACK: This is not ideal, but couldn't immediatly think of a better way when keeping a redux store in sync.
        if (emit) {
            this.signals[key]?.emit(value);
        }
        if (persist) {
            setItem(this.keyToNamespacedString(key), JSON.stringify(value));
        }
    }
    delete(key) {
        this.isLoaded();
        // reset to default
        this.currentConfig[key] = this.config[key];
        // delete from peristant storage
        deleteItem(this.keyToNamespacedString(key));
    }
    /**
     * Config should be overwritten by "higher" priority levels, arrays and records should be merged.
     *
     * 1. Config - the one for the constructor which sets default values and types
     * 2. LocalStorage - Values we have persisted for the user for use on next visit
     * 3. overrides - passed in, but could be conceived to come from CLI arguments or a file on disk in a desktop app
     * 4. URL - Values from searchParams
     *
     * We check 2-4 for valid values before updating the local mutable config
     */
    load(overrides, loadConfigFromQuery = true) {
        if (this.loaded) {
            throw new Error('config has already been loaded');
        }
        this.loaded = true;
        // load config into extractor function for typing and fallback values
        const extractValue = extractor(this.config);
        // extract params from url to pass in values to the extractor
        const { searchParams } = new URL(window.location.href);
        // loop over all the config keys to extract values
        Object.keys(this.config).forEach(key => {
            const value = extractValue({
                key,
                local: this.selectFromLocalStorage(key),
                override: overrides[key],
                params: loadConfigFromQuery
                    ? isArray(this.config[key])
                        ? searchParams.getAll(String(key))
                        : searchParams.get(String(key))
                    : null,
            });
            this.set({ key, value });
        });
        return clone(this.currentConfig);
    }
    subscribe(key, subscriber) {
        let signal = this.signals[key];
        if (!signal) {
            signal = createSignal({
                name: `config:${String(key)}`,
                // Log redaction then recognizes sensitive keys such as `displayName`.
                describeSubject: value => ({ [key]: value }),
            });
            this.signals[key] = signal;
        }
        return signal.add(subscriber);
    }
    /**
     * This fn is based on assumption that if value is not in the localstorage means that user didnt override the defaults.
     * This fns only really work on the runtime as if user change values after without saving it to local storage we will get
     * false positives.
     */
    isDefaultValue(key) {
        return this.selectFromLocalStorage(key) === null;
    }
    isLoaded() {
        if (!this.loaded) {
            throw new Error('Do not use config before loading, to ensure localeStorage and overrides are set');
        }
    }
    keyToNamespacedString(key) {
        return `${this.namespace}:${String(key)}`;
    }
    selectFromLocalStorage(key) {
        const local = getItem(this.keyToNamespacedString(key));
        if (isString(local)) {
            try {
                const parsed = JSON.parse(local);
                if (isValid(parsed)) {
                    return parsed;
                }
            }
            catch (error) {
                logger.error({ error });
            }
        }
        return local;
    }
}
//# sourceMappingURL=ConfigManager.js.map